import { LineItem } from "../../models/shared/LineItem";
import { DISCOUNT_LINE_ITEM_ID } from "./constants";

/**
 * Calculates price with VAT from price without VAT
 * @param priceWithoutVat Price without VAT
 * @param vatRate VAT rate in percentage (e.g., 25 for 25%)
 * @returns Price with VAT included
 */
export function calculatePriceWithVat(priceWithoutVat: number, vatRate: number): number {
    return priceWithoutVat * (1 + vatRate / 100);
}

/**
 * Calculates price without VAT from price with VAT
 * @param priceWithVat Price with VAT
 * @param vatRate VAT rate in percentage (e.g., 25 for 25%)
 * @returns Price without VAT
 */
export function calculatePriceWithoutVat(priceWithVat: number, vatRate: number): number {
    return vatRate ? priceWithVat / (1 + vatRate / 100) : priceWithVat;
}

/**
 * Format number as a price with proper formatting
 * @param value Number to format
 * @param decimals Number of decimal places
 * @param locale Locale to use for formatting (default: user's locale)
 * @returns Formatted price string
 */
export function formatPrice(value: number, decimals = 2, locale?: string): string {
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
    }).format(value);
}

export function formatCurrency(value: number, currency: string, locale?: string): string {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    }).format(value);
}

/**
 * Interface for line item with price calculations
 */
export interface LineItemWithCalculations {
    id: string;
    unitPrice: number;
    quantity: number;
    vatRate: number;
    discount?: number;
    total: number;
    vatAmount: number;
    totalWithVat: number;
    totalWithVatBeforeDiscount: number; // Original total with VAT before any discount
    rutSubsidyAmount: number;
    totalToPay: number;
}

/**
 * Interface for VAT breakdown by rate
 */
export interface VatBreakdown {
    rate: number;
    baseAmount: number;
    vatAmount: number;
    totalWithVat: number;
}

/**
 * Interface for the result of line items calculations
 */
export interface LineItemsCalculationResult {
    items: LineItemWithCalculations[];
    totalBeforeVat: number;
    totalVatAmount: number;
    totalWithVat: number;
    vatBreakdown: VatBreakdown[];
    totalRutSubsidyAmount: number;
    totalToPay: number;
}

/**
 * Calculate detailed information about line items including totals with and without VAT
 * @param lineItems Array of line items with basic pricing information
 * @returns Detailed calculation results for each line item and overall totals with VAT breakdown by rate
 */
export function calculateLineItemsTotal(lineItems: LineItem[]): LineItemsCalculationResult {
    // First, separate normal items and discount items
    const regularItems = lineItems.filter(item => item.id !== DISCOUNT_LINE_ITEM_ID);
    const discountItems = lineItems.filter(item => item.id === DISCOUNT_LINE_ITEM_ID);

    // Calculate total discount amount
    const totalDiscountAmount = discountItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);

    // Calculate normal line items without applying discount yet
    let regularTotalWithVat = 0;
    const regularItemsWithCalc: LineItemWithCalculations[] = regularItems.map(item => {
        const total = item.quantity * item.unitPrice;
        const vatAmount = item.vatRate ? (total * (item.vatRate / 100)) : 0;
        const itemTotalWithVat = total + vatAmount;

        // Keep track of total price with VAT for regular items
        regularTotalWithVat += itemTotalWithVat;

        return {
            id: item.id,
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            vatRate: item.vatRate,
            discount: 0, // Will be calculated later for items with VAT
            total,
            vatAmount,
            totalWithVat: itemTotalWithVat,
            totalWithVatBeforeDiscount: itemTotalWithVat, // Store original total with VAT
            rutSubsidyAmount: 0, // Will be calculated after discount
            totalToPay: itemTotalWithVat, // Will be recalculated after discount and RUT
        };
    });

    // Calculate discount line items
    const discountItemsWithCalc: LineItemWithCalculations[] = discountItems.map(item => {
        const total = item.quantity * item.unitPrice; // This will be negative

        return {
            id: item.id,
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            vatRate: 0,
            discount: 0,
            total,
            vatAmount: 0,
            totalWithVat: total, // Discount has no VAT
            totalWithVatBeforeDiscount: total, // For discount items, this is the same as totalWithVat
            rutSubsidyAmount: 0, // Discount doesn't get RUT benefit
            totalToPay: total,
        };
    });

    // Initialize counters
    let totalBeforeVat = 0;
    let totalVatAmount = 0;
    let totalWithVat = 0;
    let totalRutSubsidyAmount = 0;
    let totalToPay = 0;

    const vatRatesMap = new Map<number, VatBreakdown>();

    // Calculate discount proportions if discount exists
    const hasDiscount = regularTotalWithVat > 0 && totalDiscountAmount < 0;
    const discountProportions = new Map<string, number>();

    if (hasDiscount) {
        regularItemsWithCalc.forEach(item => {
            const proportion = item.totalWithVat / regularTotalWithVat;
            discountProportions.set(item.id, proportion);
        });
    }

    // Process regular items
    regularItemsWithCalc.forEach(item => {
        // Apply discount if needed
        let finalTotalWithVat = item.totalWithVat;
        let finalBaseAmount = item.total;
        let finalVatAmount = item.vatAmount;
        let discountAmount = 0;

        if (hasDiscount) {
            const proportion = discountProportions.get(item.id) || 0;
            discountAmount = totalDiscountAmount * proportion;
            finalTotalWithVat = item.totalWithVat + discountAmount; // discountAmount is negative

            // Recalculate base and VAT amounts after discount
            finalBaseAmount = item.vatRate ?
                finalTotalWithVat / (1 + item.vatRate / 100) :
                finalTotalWithVat;
            finalVatAmount = finalTotalWithVat - finalBaseAmount;
        }

        // Calculate RUT subsidy
        let rutSubsidyAmount = 0;
        let itemTotalToPay = finalTotalWithVat;

        const originalItem = regularItems.find(ri => ri.id === item.id);
        if (originalItem?.taxBenefit?.type === 'rut') {
            rutSubsidyAmount = originalItem.taxBenefit.rate ?
                (finalTotalWithVat * (originalItem.taxBenefit.rate / 100)) :
                0;
            itemTotalToPay = finalTotalWithVat - rutSubsidyAmount;
        }

        item.total = finalBaseAmount;
        item.vatAmount = finalVatAmount;
        item.totalWithVat = finalTotalWithVat;
        item.rutSubsidyAmount = rutSubsidyAmount;
        item.totalToPay = itemTotalToPay;

        if (hasDiscount) {
            item.discount = Math.abs(discountAmount); // Store discount as positive value
        }

        // Update overall totals
        totalBeforeVat += finalBaseAmount;
        totalVatAmount += finalVatAmount;
        totalWithVat += finalTotalWithVat;
        totalRutSubsidyAmount += rutSubsidyAmount;
        totalToPay += itemTotalToPay;

        // Update VAT breakdown
        if (item.vatRate !== 0) {
            const existingBreakdown = vatRatesMap.get(item.vatRate);
            if (existingBreakdown) {
                existingBreakdown.baseAmount += finalBaseAmount;
                existingBreakdown.vatAmount += finalVatAmount;
                existingBreakdown.totalWithVat += finalTotalWithVat;
            } else {
                vatRatesMap.set(item.vatRate, {
                    rate: item.vatRate,
                    baseAmount: finalBaseAmount,
                    vatAmount: finalVatAmount,
                    totalWithVat: finalTotalWithVat,
                });
            }
        }
    });

    // If no discount was applied, add discount items to the totals
    // If discount was applied, the values are already incorporated in the regular items
    if (!hasDiscount) {
        discountItemsWithCalc.forEach(item => {
            totalBeforeVat += item.total;
            totalWithVat += item.totalWithVat;
            totalToPay += item.totalToPay;
        });
    }

    // Combine all items
    const items = [...regularItemsWithCalc, ...discountItemsWithCalc];

    // Sort VAT rates in ascending order
    const vatBreakdown = Array.from(vatRatesMap.values()).sort((a, b) => a.rate - b.rate);

    // Return the final result
    return {
        items,
        totalBeforeVat,
        totalVatAmount,
        totalWithVat,
        vatBreakdown,
        totalRutSubsidyAmount,
        totalToPay,
    };
}
